// SPDX-License-Identifier: AGPL-3.0-or-later

import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import {snapMediaProxyImageSize} from '@app/features/messaging/utils/MediaProxyUtils';
import {buildMediaUrl, parseAvatarHash} from '@app/features/user/utils/AvatarMediaUtils';
import {isUsernameGateEscapePath} from '@app/features/user/utils/UsernameSelectionGateUtils';
import type {MediaProxyImageSize} from '@fluxer/constants/src/MediaProxyImageSizes';
import {INTEREST_ONBOARDING_MAX_SELECTIONS} from '@fluxer/schema/src/domains/interest_onboarding/InterestOnboardingRequestSchemas';
import type {
	InterestOnboardingJoinResult,
	InterestOnboardingOption,
	InterestOnboardingResponse,
} from '@fluxer/schema/src/domains/interest_onboarding/InterestOnboardingResponseSchemas';

export function isInterestGateOpen({
	dueAccountKey,
	accountKey,
	usernameMode,
	pathname,
}: {
	dueAccountKey: string | null;
	accountKey: string | null;
	usernameMode: 'none' | 'prompt' | 'required';
	pathname: string;
}): boolean {
	return (
		accountKey !== null &&
		dueAccountKey === accountKey &&
		usernameMode === 'none' &&
		!isUsernameGateEscapePath(pathname)
	);
}

export interface InterestPickerState {
	status: 'loading' | 'error' | 'ready';
	requestedRegionId: string | null;
	data: InterestOnboardingResponse | null;
	selected: ReadonlyArray<string>;
}

export type InterestPickerAction =
	| {type: 'loaded'; data: InterestOnboardingResponse}
	| {type: 'failed'}
	| {type: 'retry'}
	| {type: 'region'; regionId: string}
	| {type: 'toggle'; optionId: string};

export const INITIAL_INTEREST_PICKER_STATE: InterestPickerState = {
	status: 'loading',
	requestedRegionId: null,
	data: null,
	selected: [],
};

export function interestPickerReducer(state: InterestPickerState, action: InterestPickerAction): InterestPickerState {
	switch (action.type) {
		case 'loaded':
			return {
				...state,
				status: 'ready',
				data: action.data,
				selected: state.selected.filter((id) => action.data.options.some((option) => option.id === id)),
			};
		case 'failed':
			return {...state, status: 'error'};
		case 'retry':
			return {...state, status: 'loading'};
		case 'region':
			return {...state, status: 'loading', requestedRegionId: action.regionId, selected: []};
		case 'toggle': {
			if (state.selected.includes(action.optionId))
				return {...state, selected: state.selected.filter((id) => id !== action.optionId)};
			if (state.selected.length >= INTEREST_ONBOARDING_MAX_SELECTIONS) return state;
			if (!state.data?.options.some((option) => option.id === action.optionId)) return state;
			return {...state, selected: [...state.selected, action.optionId]};
		}
	}
}

export interface InterestOptionRun {
	group: string | null;
	start: number;
	options: Array<InterestOnboardingOption>;
}

// The server sends options in display order with each group together; a heading starts wherever the group changes.
export function interestOptionRuns(options: ReadonlyArray<InterestOnboardingOption>): Array<InterestOptionRun> {
	const runs: Array<InterestOptionRun> = [];
	options.forEach((option, index) => {
		const last = runs.at(-1);
		if (last && last.group === option.group) last.options.push(option);
		else runs.push({group: option.group, start: index, options: [option]});
	});
	return runs;
}

// A null option_id is the official guild every account joins; the picker opens a picked one instead.
export function firstJoinedGuildId(results: ReadonlyArray<InterestOnboardingJoinResult>): string | null {
	return results.find((result) => result.option_id !== null && result.status !== 'failed')?.guild_id ?? null;
}

// An option can join several guilds and options can share one, so the count is of distinct guilds. The official
// guild counts once joined, but the user never picked it, so it is not reported as failed.
export function summarizeJoinResults(results: ReadonlyArray<InterestOnboardingJoinResult>): {
	joined: number;
	failed: number;
} {
	const joined = new Set(results.filter((result) => result.status !== 'failed').map((result) => result.guild_id));
	const failed = new Set(
		results
			.filter((result) => result.option_id !== null && !joined.has(result.guild_id))
			.map((result) => result.guild_id),
	);
	return {joined: joined.size, failed: failed.size};
}

// The logo is a branding asset like the username card image (owner cosmetic-interest-<option id>), uploaded at 96-480 px.
const MIN_LOGO_SIZE: MediaProxyImageSize = 128;
const MAX_LOGO_SIZE: MediaProxyImageSize = 480;

export function interestLogoUrl(optionId: string, asset: string, cssSize: number): string {
	const endpoint = RuntimeConfig.mediaEndpoint;
	if (!endpoint) return '';
	const snapped = snapMediaProxyImageSize(cssSize);
	return buildMediaUrl({
		endpoint,
		path: 'branding',
		id: `cosmetic-interest-${optionId}`,
		hash: parseAvatarHash(asset).hash,
		size: snapped < MIN_LOGO_SIZE ? MIN_LOGO_SIZE : snapped > MAX_LOGO_SIZE ? MAX_LOGO_SIZE : snapped,
	});
}
